import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { BRAND } from "@/lib/api";

// Single component renders Terms / Privacy / Refund / DPA pages — chosen via :doc URL param.
// Public route (no auth required) so Razorpay/PayU and patients can reach them.
const DOCS = {
  terms: { title: "Terms of Service", file: "TermsContent" },
  privacy: { title: "Privacy Policy", file: "PrivacyContent" },
  refund: { title: "Refund & Cancellation Policy", file: "RefundContent" },
  dpa: { title: "Data Processing Agreement (DPA)", file: "DpaContent" },
};

export default function Legal() {
  const { doc } = useParams();
  if (!DOCS[doc]) return <Navigate to="/legal/terms" replace />;
  const meta = DOCS[doc];

  return (
    <div className="min-h-screen bg-secondary/30 py-10">
      <div className="mx-auto max-w-3xl bg-white shadow-sm rounded-lg p-8 lg:p-12">
        <div className="mb-6 flex items-center justify-between border-b border-border pb-4">
          <Link to="/" className="font-heading text-xl font-semibold text-brand" data-testid="legal-brand-link">
            {BRAND.name}
          </Link>
          <div className="flex gap-3 text-xs">
            {Object.entries(DOCS).map(([k, v]) => (
              <Link
                key={k}
                to={`/legal/${k}`}
                className={`uppercase tracking-wider ${k === doc ? "font-semibold text-brand" : "text-ink-muted hover:text-brand"}`}
                data-testid={`legal-nav-${k}`}
              >
                {k}
              </Link>
            ))}
          </div>
        </div>

        <h1 className="font-heading text-3xl font-semibold" data-testid={`legal-title-${doc}`}>{meta.title}</h1>
        <div className="mt-1 text-xs text-ink-muted">Last updated: 29 May 2026 · Effective from your subscription start date</div>

        <div className="prose prose-sm mt-6 max-w-none">
          {doc === "terms" && <TermsContent />}
          {doc === "privacy" && <PrivacyContent />}
          {doc === "refund" && <RefundContent />}
          {doc === "dpa" && <DpaContent />}
        </div>

        <div className="mt-12 border-t border-border pt-4 text-center text-[11px] text-ink-muted">
          Questions? Write to <a className="text-brand underline" href="mailto:info@stewardindia.com">info@stewardindia.com</a> · {BRAND.footer}
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="mt-6">
    <h2 className="font-heading text-lg font-semibold text-brand">{title}</h2>
    <div className="mt-2 space-y-2 text-sm leading-relaxed text-ink">{children}</div>
  </section>
);

const TermsContent = () => (
  <>
    <p>These Terms of Service govern your access to and use of <b>{BRAND.name}</b> ("Service"), a multi-tenant Eye-Care EMR / HIS Software-as-a-Service. By creating an account, you ("Customer" or "Hospital") agree to be bound by these terms.</p>
    <Section title="1. The Service">
      <p>{BRAND.name} provides cloud-based clinical, administrative and billing software for eye hospitals, clinics and multi-branch eye-care groups. Features include patient registration, OPD EMR, optometry, OT module, lab, pharmacy POS, IPD / day-care, discharge, billing, panel / insurance and reporting.</p>
    </Section>
    <Section title="2. Subscription, Plans & Trial">
      <p>The Service is offered as monthly or annual subscription tiers (Free Trial · Starter · Growth · Enterprise). A 30-day Free Trial is granted on first sign-up without payment. After the trial, continued access requires an active paid subscription. On plan expiry the system enters read-only mode for up to 30 days; data is not deleted during this window.</p>
    </Section>
    <Section title="3. Customer Responsibilities">
      <ul className="ml-4 list-disc space-y-1">
        <li>Provide accurate hospital, branch, doctor and patient information.</li>
        <li>Maintain confidentiality of login credentials. Each clinical user must have their own account — credential sharing is prohibited under NABH norms.</li>
        <li>Comply with applicable medical, regulatory and data-protection laws including the Digital Personal Data Protection Act 2023 (DPDP Act), NABH standards and IT Act 2000.</li>
        <li>Obtain valid consent from patients before recording health data in the Service.</li>
      </ul>
    </Section>
    <Section title="4. Acceptable Use">
      <p>You must not (a) reverse-engineer, copy or resell the Service, (b) upload malware or attempt to breach security, (c) use the Service to store data unrelated to the Customer's healthcare business, or (d) use the Service in a way that violates any law.</p>
    </Section>
    <Section title="5. Service Levels & Support">
      <p>We target 99.5% monthly uptime measured outside scheduled maintenance windows. Support is provided via email at <a href="mailto:info@stewardindia.com" className="text-brand">info@stewardindia.com</a>; Growth and Enterprise plans include priority response.</p>
    </Section>
    <Section title="6. Intellectual Property">
      <p>The Service, including all software, branding, templates and documentation, is owned by {BRAND.name}. You retain ownership of all clinical and patient data you upload ("Customer Data").</p>
    </Section>
    <Section title="7. Confidentiality & Data Security">
      <p>We treat Customer Data as confidential. Transport is TLS-encrypted; data is logically isolated per hospital tenant; backups are encrypted at rest. Audit logs are maintained for clinical actions for NABH compliance.</p>
    </Section>
    <Section title="8. Limitation of Liability">
      <p>To the maximum extent permitted by law, our aggregate liability for any claim is limited to the fees you paid in the 12 months preceding the claim. The Service is a clinical-aid tool — final clinical decisions rest with the treating physician.</p>
    </Section>
    <Section title="9. Termination">
      <p>Either party may terminate by 30-day notice. On termination we will provide an export of Customer Data in CSV / JSON for 60 days; thereafter data may be permanently deleted.</p>
    </Section>
    <Section title="10. Governing Law">
      <p>These Terms are governed by the laws of India. Courts at Bengaluru, Karnataka have exclusive jurisdiction.</p>
    </Section>
  </>
);

const PrivacyContent = () => (
  <>
    <p>This Privacy Policy describes how {BRAND.name} collects, uses and protects personal data when you and your patients use the Service. We comply with the Digital Personal Data Protection Act 2023 (DPDP Act), Information Technology Act 2000 and applicable NABH norms.</p>
    <Section title="1. Data we collect">
      <ul className="ml-4 list-disc space-y-1">
        <li><b>Hospital data:</b> hospital name, GSTIN, branches, addresses, billing contact.</li>
        <li><b>User data:</b> name, email, phone, role, password hash, 2FA secret (encrypted), audit-log of clinical actions.</li>
        <li><b>Patient health data ("Sensitive Personal Data"):</b> demographics, UHID, medical history, prescriptions, vitals, refraction, OT notes, discharge summaries, lab/imaging results, payments.</li>
        <li><b>Technical:</b> IP, browser, device, session timestamps for security and performance monitoring.</li>
      </ul>
    </Section>
    <Section title="2. How we use the data">
      <p>Strictly to deliver and improve the Service: clinical documentation, billing, appointment reminders, statutory reports, support, fraud prevention and security incident investigation. We <b>do not</b> sell, rent or share patient data with advertisers.</p>
    </Section>
    <Section title="3. Legal basis">
      <p>Processing of patient data is performed on behalf of the hospital as a <b>data processor</b>; the hospital is the <b>data fiduciary</b> under DPDP Act and is responsible for obtaining valid patient consent. Processing of hospital/user data is on the basis of contract performance.</p>
    </Section>
    <Section title="4. Data sharing">
      <ul className="ml-4 list-disc space-y-1">
        <li><b>Payment processors</b> (Razorpay, PayU): only the minimum data needed to take a subscription payment.</li>
        <li><b>Hosting & backup providers</b>: encrypted infrastructure for storing the Service.</li>
        <li><b>Statutory authorities</b>: where required by law, court order or NABH/ABDM mandate.</li>
      </ul>
    </Section>
    <Section title="5. Patient & user rights">
      <p>Under the DPDP Act every individual ("Data Principal") has the right to access, correct, erase or port their personal data. Patients should make such requests directly to their hospital (the data fiduciary). Hospitals may write to <a href="mailto:info@stewardindia.com" className="text-brand">info@stewardindia.com</a> for help.</p>
    </Section>
    <Section title="6. Data retention">
      <p>Patient medical records are retained as long as the hospital subscription is active, plus the statutory minimum (Indian Medical Council requires 3 years after last consultation; for medico-legal cases 10 years). On account closure we offer a 60-day export window before purging.</p>
    </Section>
    <Section title="7. Security">
      <p>All data transmitted over TLS 1.2+. Passwords hashed with bcrypt. 2FA available (and required for super-admin / hospital-admin roles). TOTP secrets encrypted at rest with Fernet. Multi-tenant isolation enforced at every API call. Audit logs are immutable. Backups encrypted and stored in a separate region.</p>
    </Section>
    <Section title="8. Cookies">
      <p>We use only essential cookies (session, CSRF, language preference). No third-party tracking or advertising cookies are set.</p>
    </Section>
    <Section title="9. Contact / Grievance Officer">
      <p>Grievance Officer / Data Protection Officer: <a href="mailto:info@stewardindia.com" className="text-brand">info@stewardindia.com</a>. We respond to verified requests within 30 days.</p>
    </Section>
  </>
);

const RefundContent = () => (
  <>
    <p>This policy applies to all paid subscriptions of {BRAND.name}.</p>
    <Section title="1. 30-day Free Trial">
      <p>Every new hospital is granted a 30-day Free Trial on first sign-up. No card is required, no payment is taken. You can start using the Service immediately.</p>
    </Section>
    <Section title="2. Refund window for paid plans">
      <p>You may request a full refund within <b>7 calendar days</b> of your first paid invoice if you have not actively used the platform (defined as &lt; 10 patient records created or &lt; 5 consultations saved). Refunds are processed in 7–10 business days to the original payment method.</p>
    </Section>
    <Section title="3. Non-refundable items">
      <ul className="ml-4 list-disc space-y-1">
        <li>Renewal payments after 7 days of the invoice date.</li>
        <li>Per-branch / per-user add-ons that have been actively used.</li>
        <li>SMS, WhatsApp and email-credit top-ups (consumable).</li>
        <li>Customisation, training or implementation fees once delivered.</li>
      </ul>
    </Section>
    <Section title="4. Cancellation">
      <p>You may cancel your subscription anytime from <b>Settings → Subscription → Cancel</b>. Cancellation stops auto-renewal at the end of the current billing cycle; you keep access until that date. No prorated refund is given for partial periods unless covered by section 2.</p>
    </Section>
    <Section title="5. Failed payments / dunning">
      <p>If a renewal payment fails, we will retry the charge on day 3, day 7 and day 14 with email notifications. On day 30 the account moves to read-only mode. Data is not deleted for at least 60 days post expiry to allow recovery.</p>
    </Section>
    <Section title="6. How to request a refund">
      <p>Email <a href="mailto:info@stewardindia.com" className="text-brand">info@stewardindia.com</a> with the hospital name, GSTIN and invoice number. We aim to acknowledge within 2 business days and resolve within 10.</p>
    </Section>
  </>
);

const DpaContent = () => (
  <>
    <p>This Data Processing Agreement ("DPA") forms part of the Terms of Service between {BRAND.name} ("Processor") and the Customer Hospital ("Controller / Data Fiduciary"). It governs the processing of personal data, including sensitive personal data (health data) under the Digital Personal Data Protection Act 2023.</p>
    <Section title="1. Roles">
      <p>The <b>Hospital</b> is the Data Fiduciary for patient data and is responsible for obtaining valid consent. {BRAND.name} acts only as a Data Processor, processing data on documented instructions from the Hospital, as embodied in the Service.</p>
    </Section>
    <Section title="2. Subject-matter & duration">
      <p>Processing covers the operation of the Service for the term of the subscription, plus a 60-day post-termination data-retention window for export.</p>
    </Section>
    <Section title="3. Nature & purpose">
      <p>Storing, organising, retrieving, transmitting and erasing personal data necessary for clinical documentation, billing, statutory reporting, security and customer support.</p>
    </Section>
    <Section title="4. Categories of data subjects">
      <p>Patients (incl. minors via guardian), hospital users (doctors, optometrists, nurses, billing, admin, super-admin).</p>
    </Section>
    <Section title="5. Sub-processors">
      <p>Hosting (AWS / Emergent platform), database (MongoDB Atlas), SMTP (Gmail Workspace), payment (Razorpay / PayU), SMS (MSG91 / TextLocal). All sub-processors are bound by equivalent confidentiality and security obligations. We notify Customers of any new sub-processor 30 days in advance.</p>
    </Section>
    <Section title="6. Security measures">
      <p>TLS 1.2+ in transit; bcrypt + AES (Fernet) at rest for credentials & 2FA secrets; per-tenant database isolation; role-based access control; audit logs; 99.5% uptime target; encrypted off-site daily backups with 30-day retention.</p>
    </Section>
    <Section title="7. Breach notification">
      <p>We will notify the Hospital and (where required) the Data Protection Board of India within 72 hours of becoming aware of a personal-data breach, providing nature of breach, categories & approximate number of records affected, and mitigation measures.</p>
    </Section>
    <Section title="8. International transfers">
      <p>All primary data is hosted in India. Disaster-recovery snapshots may briefly transit through Singapore / EU regions with equivalent contractual safeguards in place.</p>
    </Section>
    <Section title="9. Data-subject rights assistance">
      <p>We provide tooling (CSV / JSON export, deletion API) so the Hospital can fulfil access, correction, portability and erasure requests within the timelines mandated by the DPDP Act.</p>
    </Section>
    <Section title="10. Audit">
      <p>The Hospital may, with reasonable notice and during business hours, audit our compliance with this DPA — including third-party reports, ISO/SOC certifications when available — once per year.</p>
    </Section>
  </>
);
